import { useState, useMemo } from "react";
import { Header } from "../components/promill/Header";
import { Footer } from "../components/promill/Footer";
import { ProductCard } from "../components/promill/FeaturedProducts";
import { PRODUCTS } from "../data/promill";
import { Search, SlidersHorizontal } from "lucide-react";

const CATEGORIES = ["All", "Bowls", "Wraps", "Salads", "Smoothies"];
const DIETS = [
    { id: "veg", label: "Veg" },
    { id: "vegan", label: "Vegan" },
    { id: "non-veg", label: "Non-Veg" },
    { id: "high-protein", label: "High Protein" },
    { id: "high-fiber", label: "High Fiber" },
    { id: "keto", label: "Keto" },
];

const SORTS = [
    { id: "popular", label: "Popular" },
    { id: "price-asc", label: "Price: Low → High" },
    { id: "price-desc", label: "Price: High → Low" },
    { id: "protein-desc", label: "Highest Protein" },
];

const MenuPage = () => {
    const [search, setSearch] = useState("");
    const [category, setCategory] = useState("All");
    const [diet, setDiet] = useState([]);
    const [sort, setSort] = useState("popular");

    const toggleDiet = (id) =>
        setDiet((d) => (d.includes(id) ? d.filter((x) => x !== id) : [...d, id]));

    const filtered = useMemo(() => {
        let list = PRODUCTS;
        if (category !== "All") list = list.filter((p) => p.category === category);
        if (diet.length) list = list.filter((p) => diet.every((d) => p.diet.includes(d)));
        if (search) {
            const q = search.toLowerCase();
            list = list.filter((p) => p.name.toLowerCase().includes(q) || p.tagline.toLowerCase().includes(q));
        }
        if (sort === "price-asc") list = [...list].sort((a, b) => a.price - b.price);
        else if (sort === "price-desc") list = [...list].sort((a, b) => b.price - a.price);
        else if (sort === "protein-desc") list = [...list].sort((a, b) => b.protein - a.protein);
        return list;
    }, [search, category, diet, sort]);

    return (
        <div data-testid="menu-page" className="bg-pm-cream min-h-screen">
            <Header />
            <section className="bg-pm-cream pt-10 md:pt-14 pb-6">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <span className="inline-block text-pm-orange font-bold tracking-[0.25em] uppercase text-[11px] mb-3">
                        Our Menu
                    </span>
                    <h1 className="text-4xl md:text-6xl font-extrabold tracking-[-0.02em] text-pm-black leading-tight">
                        Bowls, wraps, salads <span className="text-pm-green">&amp; shakes.</span>
                    </h1>
                    <p className="mt-3 text-pm-black/65 max-w-2xl">
                        Every meal capped at 500 kcal. Minimum 22g of clean protein. Always made with Indian millets.
                    </p>
                </div>
            </section>

            {/* Filters bar */}
            <section className="bg-pm-cream pb-6 sticky top-20 z-30 border-b border-pm-black/5">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-center">
                        <div className="relative flex-1">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-pm-black/40" />
                            <input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search wraps, bowls, smoothies…"
                                data-testid="menu-search-input"
                                className="w-full h-12 pl-12 pr-4 rounded-full bg-white border border-pm-black/10 focus:outline-none focus:ring-2 focus:ring-pm-yellow"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <SlidersHorizontal className="w-5 h-5 text-pm-black/60" />
                            <select
                                value={sort}
                                onChange={(e) => setSort(e.target.value)}
                                data-testid="menu-sort-select"
                                className="h-12 px-4 rounded-full bg-white border border-pm-black/10 font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-pm-yellow"
                            >
                                {SORTS.map((s) => (
                                    <option key={s.id} value={s.id}>{s.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2">
                        {CATEGORIES.map((c) => (
                            <button
                                key={c}
                                onClick={() => setCategory(c)}
                                data-testid={`category-${c.toLowerCase()}`}
                                className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${
                                    category === c
                                        ? "bg-pm-green text-pm-yellow"
                                        : "bg-white text-pm-black border border-pm-black/10 hover:border-pm-green"
                                }`}
                            >
                                {c}
                            </button>
                        ))}
                        <div className="w-px bg-pm-black/10 mx-2" />
                        {DIETS.map((d) => (
                            <button
                                key={d.id}
                                onClick={() => toggleDiet(d.id)}
                                data-testid={`diet-${d.id}`}
                                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                                    diet.includes(d.id)
                                        ? "bg-pm-yellow text-pm-black border border-pm-black/15"
                                        : "bg-transparent text-pm-black/70 border border-pm-black/10 hover:border-pm-yellow hover:text-pm-black"
                                }`}
                            >
                                {d.label}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Results */}
            <section className="py-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-sm text-pm-black/60 mb-6" data-testid="results-count">
                        {filtered.length} {filtered.length === 1 ? "item" : "items"}
                    </div>
                    {filtered.length === 0 ? (
                        <div className="bg-white rounded-2xl border border-pm-black/8 p-12 text-center" data-testid="empty-state">
                            <p className="text-lg font-bold text-pm-black">No matches. Try clearing filters.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {filtered.map((p) => <ProductCard key={p.id} product={p} />)}
                        </div>
                    )}
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default MenuPage;
